import classNames from "classnames/dedupe";
import React, { FC, useState, useMemo } from "react";
import {
  MapCanvas,
  ScrollContainer,
  SvgIcon,
  Row,
  Column,
  Button,
  SelectorOption,
  TSelectorOption,
  DropdownAlignment,
} from "../../components";
import LinkButton from "../../components/ActionsSelectors/Button/LinkButton/LinkButton";
import { Select } from "../../components/ActionsSelectors/Select";
import { ValueOf } from "../../helpers";

import useWindowDimensions from "../../hooks/useWindowDimensions";

import { ListMediumIcon, LocationPinIcon } from "../../icons";
import { sizePickerData } from "../../mock/filtersMock";
import { storeDetails } from "../../mock/storeDetails";
import { mockCities } from "../../mock/searchData";
import MapSearchInput from "./MapSearchInput";
import StoreDetails from "./StoreDetails";

export interface IStoreAvailability {
  onClose?(): void;
}

const StoreAvailability: FC<IStoreAvailability> = ({ onClose }) => {
  const breakPoint = 768;

  const mapInit = {
    defaultCenter: {
      lat: 52.0869966,
      lng: 4.8476856,
    },
    options: {
      disableDefaultUI: true,
    },
    defaultZoom: 9,
  };

  const [selectedStore, setSelectedStore] = useState<number>(null);
  const { viewportWidth } = useWindowDimensions();
  const [showFullList, setShowFullList] = useState<boolean>(false);

  const onShowFullList = () => {
    setShowFullList(!showFullList);
  };

  const toggleStoreDetailsOnMap = () => {
    setSelectedStore(1);
  };

  const handleClose = () => {
    onClose && onClose();
  };

  const handleCloseDetails = (e) => {
    e.stopPropagation();
    setSelectedStore(null);
  };

  const handleAddToBag = () => {
    return null;
  };

  const handleSelectStore = (event, id: number) => {
    setSelectedStore(id);
  };

  const rootClasses = classNames(
    "align-items-start position-relative h-vh-100 m-0 availability-container",
    {
      "availability-container--with-map":
        !showFullList && viewportWidth < breakPoint,
    },
  );

  const [size, setSize] = useState<ValueOf<TSelectorOption>>();

  const [mapSearchValue, setMapSearchValue] = useState<string>("");

  const handleMapSearchSelection = (x) => {
    // eslint-disable-next-line no-alert
    alert(`selected option: ${x}`);
  };

  const searchResults = useMemo(
    () =>
      mockCities.filter(
        (item) =>
          mapSearchValue &&
          item.label &&
          item.label.toLowerCase().includes(mapSearchValue.toLowerCase()),
      ),
    [mapSearchValue],
  );

  return (
    <Row
      classes={{
        root: rootClasses,
      }}
    >
      <Column classes={{ root: "availability-map p-0 flex-grow-1" }}>
        <MapCanvas
          {...mapInit}
          places={storeDetails}
          onMarkerClick={toggleStoreDetailsOnMap}
        />
      </Column>

      <Column classes={{ root: "availability-sidebar p-0" }}>
        <div className="availability-info">
          <div className="d-flex mx-6 align-items-center">
            <MapSearchInput
              classes={{ dropdown: { button: "flex-grow-1" } }}
              placeHolder="Enter Zipcode or City/State"
              value={mapSearchValue}
              onChange={setMapSearchValue}
              onSelect={handleMapSearchSelection}
              searchResults={searchResults}
            />
            <Button
              variant="icon"
              onClick={handleClose}
              classes={{ root: "ms-4" }}
            >
              <SvgIcon type="svg-icon" icon="ExitIcon" isButton />
            </Button>
          </div>

          <div className="d-flex align-items-baseline flex-column flex-sm-row justify-content-between my-4 mx-6 m-sm-6 mb-sm-4">
            <div className="d-flex align-items-start">
              <div className="mb-6 mb-lg-6">
                <Select<TSelectorOption>
                  theme="dark"
                  variant="rounded"
                  valueKey="id"
                  labelKey="title"
                  titlePrefix="Size "
                  placeholder={sizePickerData?.title}
                  mobilePlaceholder={sizePickerData?.mobileTitle}
                  noOptionsPlaceholder={sizePickerData?.noItemsText}
                  options={sizePickerData?.items}
                  customOptionRender={SelectorOption}
                  renderOptionsHeader={() => (
                    <div className="selector-header--mobile">
                      <LinkButton>Find my size</LinkButton>
                      <LinkButton>Size Chart</LinkButton>
                    </div>
                  )}
                  value={size}
                  alignment={DropdownAlignment.START}
                  onChange={setSize}
                />
              </div>

              <Button
                variant="secondary"
                onClick={onShowFullList}
                classes={{ root: "btn-sm-with-icon d-md-none ms-4" }}
              >
                {showFullList ? (
                  <>
                    <LocationPinIcon className="me-3" /> View Map
                  </>
                ) : (
                  <>
                    <ListMediumIcon className="me-3" /> View List
                  </>
                )}
              </Button>
            </div>
          </div>

          <ScrollContainer classes={{ root: "uds-scroll--transparent" }}>
            <ul className="stores-list">
              {storeDetails.map((store) => (
                <StoreDetails
                  key={store.id}
                  isActive={selectedStore === store.id}
                  handleClose={(e) => handleCloseDetails(e)}
                  handleSelectStore={(e) => handleSelectStore(e, store.id)}
                  onSelectButtonClick={handleAddToBag}
                  isWithSelectButton
                  {...store}
                />
              ))}
            </ul>
          </ScrollContainer>
        </div>
      </Column>
    </Row>
  );
};

export default StoreAvailability;
